import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  FormActionField,
  FormDesignerSection,
  FormDesignerAction,
  FORM_FIELD_SETTINGS_MODAL_TYPE,
  FormSectionType,
} from 'forms/form-designer/Constants';
import Label from 'ui-library/bs/Label';
import Checkbox from 'ui-library/Checkbox';
import {WHITE} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ReorderableSimpleTable, {
  reorderableSimpleTableStyles,
} from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row as TableRow} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import runModal from 'ui-library/runModal';

import {FormFieldSettingsModal} from './FormFieldSettingsModal';
import {arrayMove} from './util';

export interface FormDesignerFieldsTableProps {
  action: FormDesignerAction;
  sections: Array<FormDesignerSection>;
  onFieldChange: (identifier: string, updatedField: FormActionField) => void;
  onFieldsChange: (identifier: string, updatedFields: Array<FormActionField>) => void;

  // Data for analytics payload
  formDefinitionId: string | null;
  sessionId: string;
  actionOrigin: ActionOrigin;
}

export function FormDesignerFieldsTable({
  action,
  sections,
  onFieldChange,
  onFieldsChange,
  formDefinitionId,
  sessionId,
  actionOrigin,
}: FormDesignerFieldsTableProps): JSX.Element {
  const {fields} = action;
  const sectionsMap = useMemo(
    () => objectFromEntries(sections.map((section, index) => [section.key, {...section, index}])),
    [sections]
  );

  function openFieldSettingsModal(field: FormActionField): void {
    runModal(FormFieldSettingsModal, {
      field,
      onRequestSubmit: (updatedField: FormActionField) => {
        onFieldChange(action.key, updatedField);
      },
      formDefinitionId,
      actionOrigin,
      sessionId,
    });
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.FORMS, {
      session_id: sessionId,
      action_origin: actionOrigin,
      modal_type: FORM_FIELD_SETTINGS_MODAL_TYPE,
      form_definition_id: formDefinitionId,
      form_field_key: field.key,
      schema_field_api_id: field.actionParameter.schemaFieldId,
      action_parameter_id: field.actionParameter.id,
      schema_field_name: field.actionParameter.name,
      field_required: field.required,
      field_included: field.included,
    });
  }

  const getActionParameterRows = (): Array<TableRow> => {
    if (!action.selectedSchema) {
      return [];
    }
    return fields.map((field: FormActionField) => {
      const assignedSection = sectionsMap[field.sectionKey];
      const isSnapshotField = field.actionParameter.isSnapshot;
      return {
        id: field.key,
        cells: [
          {
            key: 'checkbox',
            contentsClassName: css(styles.iconCell),
            contents: (
              <Checkbox
                checked={field.included ?? false}
                disabled={field.actionParameter.isRequired}
                onChange={() => {
                  onFieldChange(action.key, {
                    ...field,
                    included: !field.included,
                  });
                }}
              />
            ),
            cellClassName: css(styles.smallColumn),
          },
          {
            key: 'name',
            contents: <div>{field.displayName}</div>,
            cellClassName: css(reorderableSimpleTableStyles.wideColumn),
          },
          {
            key: 'section',
            contents: (
              <SelectDropdown
                disabled={!field.included}
                displayName={
                  assignedSection ? (
                    formatIndexedSectionName(assignedSection.name, assignedSection.index)
                  ) : (
                    <em>No Section</em>
                  )
                }
                menu={
                  <Menu value={field.sectionKey} width={Menu.WIDTHS.FULL}>
                    {sections.map((section, index) => (
                      <Option
                        key={section.key}
                        value={section.key}
                        isDisabled={isSnapshotField && section.sectionType === FormSectionType.STATIC}
                        onSelect={(sectionKey: string) => {
                          onFieldChange(action.key, {
                            ...field,
                            sectionKey,
                          });
                        }}
                      >
                        {formatIndexedSectionName(section.name, index)}
                      </Option>
                    ))}
                  </Menu>
                }
              />
            ),
            cellClassName: css(reorderableSimpleTableStyles.wideColumn),
          },
          {
            key: 'settings',
            contentsClassName: css(styles.iconCell),
            contents: (
              <SettingsIcon
                disabled={!field.included}
                onClick={() => {
                  openFieldSettingsModal(field);
                }}
              />
            ),
            cellClassName: css(styles.smallColumn),
          },
        ],
      };
    });
  };
  return (
    <div className="u-spacing-bottom">
      <Label>Fields to include</Label>
      <div className={css(styles.table)}>
        <ReorderableSimpleTable
          headerCells={[
            {
              key: 'checkbox',
              contents: <div>Include</div>, // TODO: mocks show a checkbox which I think is for a select all?
              cellClassName: css(styles.smallColumn),
            },
            {
              key: 'Field name',
              contents: <div>Field name</div>,
              cellClassName: css(reorderableSimpleTableStyles.wideColumn),
            },
            {
              key: 'section',
              contents: <div>Section</div>,
              cellClassName: css(reorderableSimpleTableStyles.wideColumn),
            },
            {
              key: 'settings',
              contents: null,
              cellClassName: css(styles.smallColumn),
            },
          ]}
          isDraggingDisabled={false}
          noRowsDisplay="No fields found for this schema."
          rows={getActionParameterRows()}
          onReorderRow={(key: string, newIndex: number) => {
            onFieldsChange(
              action.key,
              arrayMove(
                fields,
                fields.findIndex((f) => f.key === key),
                newIndex
              )
            );
          }}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  smallColumn: {
    flex: '0 0 50px',
  },
  iconCell: {
    lineHeight: 0,
  },
  table: {
    backgroundColor: WHITE,
    borderRadius: 4,
  },
});

function formatIndexedSectionName(name: string, index: number): string {
  return name ? `${++index} (${name})` : `${++index}`;
}
